import { Batcher } from "@/sdk/transport/batcher"
import { sessionIdentity } from "@/sdk/transport/identity"

// Matches Page::ProgressStatus::MAX_PAGES.
const BATCH_LIMIT = 50

// Batched reads travel as repeated query params; this keeps the encoded URL
// well under the 8KB most edges allow, with headroom for the base URL.
export const MAX_READ_URL_BYTES = 6000
const PARAM_OVERHEAD = "&pages[]=".length

export type ProgressSubject = {
  type: "page" | "occurrence"
  id: string
  path: string
}

export type ProgressRow = {
  subject: ProgressSubject
  started: boolean
  startedAt: string | null
  completed: boolean
  completedAt: string | null
  percent: number
}

type Options = {
  read: (paths: string[]) => Promise<{ results?: ProgressRow[] } | undefined>
  onRows: (rows: ProgressRow[]) => void
  windowMs?: number
}

/**
 * A reactive read asks for one page's progress at a time, so a rendered list
 * asks for dozens of them in one tick. Collects them by path — the server
 * resolves — and reads them in one request. An unknown path has no row.
 */
export class ProgressBatch {
  private readonly batcher: Batcher<string, ProgressRow | null>

  constructor({ read, onRows, windowMs }: Options) {
    this.batcher = new Batcher<string, ProgressRow | null>({
      cap: BATCH_LIMIT,
      windowMs,
      sizeOf: path => encodeURIComponent(path).length + PARAM_OVERHEAD,
      maxSize: MAX_READ_URL_BYTES,
      identity: sessionIdentity,
      flush: async paths => {
        const data = await read(Array.from(new Set(paths)))
        const rows = data?.results ?? []
        onRows(rows)

        const byPath = new Map(rows.map(row => [row.subject.path, row]))
        return paths.map(path => ({
          ok: true,
          value: byPath.get(path) ?? null
        }))
      }
    })
  }

  fetch(path: string): Promise<ProgressRow | null> {
    return this.batcher.push(path)
  }

  destroy(): void {
    this.batcher.destroy()
  }
}
